/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 * 
 * Spacing and Sizing System - Consistent spacing and sizing scale
 * Based on 4px base unit for better alignment
 */

export const spacing = {
    // Spacing Scale (based on 4px unit)
    0: '0',
    1: '0.25rem',   // 4px
    2: '0.5rem',    // 8px
    3: '0.75rem',   // 12px
    4: '1rem',      // 16px
    5: '1.25rem',   // 20px
    6: '1.5rem',    // 24px
    7: '1.75rem',   // 28px
    8: '2rem',      // 32px
    9: '2.25rem',   // 36px
    10: '2.5rem',   // 40px
    11: '2.75rem',  // 44px
    12: '3rem',     // 48px
    14: '3.5rem',   // 56px
    16: '4rem',     // 64px
    20: '5rem',     // 80px
    24: '6rem',     // 96px
    28: '7rem',     // 112px
    32: '8rem',     // 128px
    36: '9rem',     // 144px
    40: '10rem',    // 160px
    44: '11rem',    // 176px
    48: '12rem',    // 192px
    52: '13rem',    // 208px
    56: '14rem',    // 224px
    60: '15rem',    // 240px
    64: '16rem',    // 256px
    72: '18rem',    // 288px
    80: '20rem',    // 320px
    96: '24rem',    // 384px
} as const;

export const sizing = {
    // Common Size Units
    xs: '20rem',    // 320px
    sm: '24rem',    // 384px
    md: '28rem',    // 448px
    lg: '32rem',    // 512px
    xl: '36rem',    // 576px
    '2xl': '42rem', // 672px
    '3xl': '48rem', // 768px
    '4xl': '56rem', // 896px
    '5xl': '64rem', // 1024px
    '6xl': '72rem', // 1152px
    '7xl': '80rem', // 1280px
    full: '100%',
    screen: '100vw',
    min: 'min-content',
    max: 'max-content',
    fit: 'fit-content',
} as const;

// Padding Preset Combinations
export const paddingPresets = {
    xs: spacing[1],              // 4px
    sm: spacing[2],              // 8px
    md: spacing[4],              // 16px
    lg: spacing[6],              // 24px
    xl: spacing[8],              // 32px
    compact: `${spacing[2]} ${spacing[4]}`,         // 8px 16px
    normal: `${spacing[4]} ${spacing[6]}`,          // 16px 24px
    spacious: `${spacing[6]} ${spacing[8]}`,        // 24px 32px
} as const;

// Margin Preset Combinations
export const marginPresets = {
    xs: spacing[1],              // 4px
    sm: spacing[2],              // 8px
    md: spacing[4],              // 16px
    lg: spacing[6],              // 24px
    xl: spacing[8],              // 32px
    auto: 'auto',
    compact: `${spacing[2]} ${spacing[4]}`,         // 8px 16px
    normal: `${spacing[4]} ${spacing[6]}`,          // 16px 24px
    spacious: `${spacing[6]} ${spacing[8]}`,        // 24px 32px
} as const;

// Gap Preset Combinations (for flexbox/grid)
export const gapPresets = {
    xs: spacing[1],              // 4px
    sm: spacing[2],              // 8px
    md: spacing[4],              // 16px
    lg: spacing[6],              // 24px
    xl: spacing[8],              // 32px
    compact: spacing[2],         // 8px
    normal: spacing[4],          // 16px
    spacious: spacing[6],        // 24px
} as const;

export type Spacing = typeof spacing;
export type Sizing = typeof sizing;
